import type { Step } from 'react-joyride'
import { createContext, useCallback, useContext, useMemo, useState, type JSX, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export type TutorialKey = 'list' | 'detail' | 'finalize'

interface OnboardingContextValue {
  isTourActive: boolean
  stepIndex: number
  activeTutorial: TutorialKey | null
  steps: Step[]
  isHelpMode: boolean
  allFeatureSteps: Step[]
  startTour: (key: TutorialKey) => void
  stopTour: () => void
  openHelp: () => void
  stopHelp: () => void
  setStepIndex: (index: number) => void
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

interface Props {
  children: ReactNode
}

/** Finds the first grid row (by rowindex) that contains a specific data-tour button inside */
const firstRowButton = (tourName: string): HTMLElement | null => {
  const rows = Array.from(document.querySelectorAll<HTMLElement>('.MuiDataGrid-row[data-rowindex]'))
  rows.sort((a, b) => Number(a.dataset.rowindex ?? 0) - Number(b.dataset.rowindex ?? 0))
  for (const row of rows) {
    const btn = row.querySelector<HTMLElement>(`[data-tour="${tourName}"]`)
    if (btn) return btn
  }
  return null
}

const OnboardingProvider = ({ children }: Props): JSX.Element => {
  const { t, i18n } = useTranslation()
  const [isTourActive, setIsTourActive] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [activeTutorial, setActiveTutorial] = useState<TutorialKey | null>(null)
  const [isHelpMode, setIsHelpMode] = useState(false)

  const tutorials = useMemo<Record<TutorialKey, Step[]>>(
    () => ({
      list: [
        {
          target: 'body',
          placement: 'center',
          title: t('tour.tutorials.list.intro.title'),
          content: t('tour.tutorials.list.intro.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="import-button"]',
          placement: 'bottom-end',
          title: t('tour.steps.importManual.title'),
          content: t('tour.steps.importManual.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="search-and-filters"]',
          placement: 'bottom',
          title: t('tour.steps.searchAndFilters.title'),
          content: t('tour.steps.searchAndFilters.content'),
          disableBeacon: true,
        },
        {
          target: '.MuiDataGrid-row[data-rowindex="0"] [data-tour="status-cell"]',
          placement: 'right',
          title: t('tour.steps.statuses.title'),
          content: t('tour.steps.statuses.content'),
          disableBeacon: true,
          skipScroll: true,
        },
        {
          target: 'body',
          placement: 'center',
          title: t('tour.steps.failedStatus.title'),
          content: t('tour.steps.failedStatus.content'),
          disableBeacon: true,
        },
        {
          target: '.MuiDataGrid-row[data-rowindex="0"] [data-tour="tags-cell"]',
          placement: 'bottom',
          title: t('tour.steps.tags.title'),
          content: t('tour.steps.tags.content'),
          disableBeacon: true,
          skipScroll: true,
        },
        {
          target: '[data-tour="assign-button"]',
          placement: 'bottom',
          title: t('tour.steps.assign.title'),
          content: t('tour.steps.assign.content'),
          disableBeacon: true,
        },
        {
          target: () => firstRowButton('action-btn-message'),
          placement: 'left',
          title: t('tour.steps.actionMessage.title'),
          content: t('tour.steps.actionMessage.content'),
          disableBeacon: true,
          skipScroll: true,
        },
        {
          target: '.MuiDataGrid-row[data-rowindex="0"] [data-tour="action-btn-link"]',
          placement: 'left',
          title: t('tour.steps.actionLink.title'),
          content: t('tour.steps.actionLink.content'),
          disableBeacon: true,
          skipScroll: true,
        },
        {
          target: () => firstRowButton('action-btn-delete'),
          placement: 'left',
          title: t('tour.steps.deleteRestore.title'),
          content: t('tour.steps.deleteRestore.content'),
          disableBeacon: true,
          skipScroll: true,
        },
        {
          target: 'body',
          placement: 'center',
          title: t('tour.tutorials.list.outro.title'),
          content: t('tour.tutorials.list.outro.content'),
          disableBeacon: true,
        },
      ],
      detail: [
        {
          target: 'body',
          placement: 'center',
          title: t('tour.tutorials.detail.intro.title'),
          content: t('tour.tutorials.detail.intro.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="detail-breadcrumbs"]',
          placement: 'bottom-start',
          title: t('tour.steps.detailBreadcrumbs.title'),
          content: t('tour.steps.detailBreadcrumbs.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="document-notes-btn"]',
          placement: 'bottom-end',
          title: t('tour.steps.match.title'),
          content: t('tour.steps.match.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="document-notes-btn"]',
          placement: 'bottom-end',
          title: t('tour.steps.unlink.title'),
          content: t('tour.steps.unlink.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="message-btn"]',
          placement: 'bottom',
          title: t('tour.steps.messageButton.title'),
          content: t('tour.steps.messageButton.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="regen-sas-btn"]',
          placement: 'bottom',
          title: t('tour.steps.regenSas.title'),
          content: t('tour.steps.regenSas.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="retry-btn"]',
          placement: 'bottom',
          title: t('tour.steps.retry.title'),
          content: t('tour.steps.retry.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="page-card"]',
          placement: 'right',
          title: t('tour.steps.pageZoom.title'),
          content: t('tour.steps.pageZoom.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="edit-mode-btn"]',
          placement: 'bottom',
          title: t('tour.steps.documentType.title'),
          content: t('tour.steps.documentType.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="page-controls"]',
          placement: 'bottom-end',
          title: t('tour.steps.pageControls.title'),
          content: t('tour.steps.pageControls.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="doctype-filter"]',
          placement: 'bottom',
          title: t('tour.steps.doctypeFilter.title'),
          content: t('tour.steps.doctypeFilter.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="extracted-data-panel"]',
          placement: 'left',
          title: t('tour.steps.extractedData.title'),
          content: t('tour.steps.extractedData.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="document-nav"]',
          placement: 'bottom-end',
          title: t('tour.steps.documentNav.title'),
          content: t('tour.steps.documentNav.content'),
          disableBeacon: true,
        },
        {
          target: 'body',
          placement: 'center',
          title: t('tour.tutorials.detail.outro.title'),
          content: t('tour.tutorials.detail.outro.content'),
          disableBeacon: true,
        },
      ],
      finalize: [
        {
          target: 'body',
          placement: 'center',
          title: t('tour.tutorials.finalize.intro.title'),
          content: t('tour.tutorials.finalize.intro.content'),
          disableBeacon: true,
        },
        {
          target: '[data-tour="finalize-btn"]',
          placement: 'bottom',
          title: t('tour.steps.finalize.title'),
          content: t('tour.steps.finalize.content'),
          disableBeacon: true,
        },
        {
          target: 'body',
          placement: 'center',
          title: t('tour.tutorials.finalize.outro.title'),
          content: t('tour.tutorials.finalize.outro.content'),
          disableBeacon: true,
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.resolvedLanguage],
  )

  const steps = useMemo<Step[]>(
    () => (activeTutorial ? tutorials[activeTutorial] : []),
    [activeTutorial, tutorials],
  )

  const allFeatureSteps = useMemo<Step[]>(
    () =>
      [...tutorials.list, ...tutorials.detail, ...tutorials.finalize].filter(
        (s) => s.placement !== 'center',
      ),
    [tutorials],
  )

  const startTour = useCallback((key: TutorialKey) => {
    setIsHelpMode(false)
    setActiveTutorial(key)
    setStepIndex(0)
    setIsTourActive(true)
  }, [])

  const stopTour = useCallback(() => {
    setIsTourActive(false)
    setStepIndex(0)
    setActiveTutorial(null)
  }, [])

  const openHelp = useCallback(() => {
    setIsTourActive(false)
    setActiveTutorial(null)
    setIsHelpMode(true)
  }, [])

  const stopHelp = useCallback(() => {
    setIsHelpMode(false)
  }, [])

  const value = useMemo<OnboardingContextValue>(
    () => ({
      isTourActive,
      stepIndex,
      activeTutorial,
      steps,
      isHelpMode,
      allFeatureSteps,
      startTour,
      stopTour,
      openHelp,
      stopHelp,
      setStepIndex,
    }),
    [isTourActive, stepIndex, activeTutorial, steps, isHelpMode, allFeatureSteps, startTour, stopTour, openHelp, stopHelp],
  )

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}

export const useOnboarding = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContext)
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider')
  return ctx
}

export default OnboardingProvider
